import {
  CSSProperties,
  KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Loader2,
  MessageCircle,
  Send,
  SquarePen,
  User,
  Users,
  X,
} from 'lucide-react';
import {
  fetchConversationMessages,
  fetchConversations,
  fetchMessagingContacts,
  markConversationRead,
  MESSAGING_REFRESH_MS,
  MessagingConversation,
  MessagingConversationKind,
  MessagingMember,
  MessagingMessage,
  MessagingThread,
  openDirectConversation,
  openOfficeConversation,
  sendConversationMessage,
} from '../api/candidate';
import dayjs, {
  fmtDate,
  fmtDayLong,
  fmtDayShort,
  fmtTime,
  isSameDay,
  isToday,
} from '../lib/dates';

const CONVERSATIONS_KEY = ['candidate', 'messaging', 'conversations'];
const threadKey = (id: string) => ['candidate', 'messaging', 'thread', id];
const MAX_BODY_LENGTH = 4000;
/** Espace laissé sous la carte : marges basses de la vue (pb-20) et de la page (py-8). */
const BOTTOM_RESERVE = 112;

/** Message du serveur, affiché tel quel ; à défaut, un texte de repli. */
function serverMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown } | undefined)
      ?.message;
    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message.length > 0) return message.join(' ');
  }
  return fallback;
}

/** Conversation retirée ou accès fermé : insister n'y changera rien. */
function isGone(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    [403, 404].includes(error.response?.status ?? 0)
  );
}

/** UUID v4 ; `randomUUID` n'existe qu'en contexte sécurisé (HTTPS, localhost). */
function newRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
}

/** Réunit des messages sans doublon, du plus ancien au plus récent. */
function mergeMessages(
  a: MessagingMessage[],
  b: MessagingMessage[],
): MessagingMessage[] {
  const byId = new Map<string, MessagingMessage>();
  for (const message of a) byId.set(message.id, message);
  for (const message of b) byId.set(message.id, message);
  return [...byId.values()].sort((x, y) => x.sequence - y.sequence);
}

/**
 * Relecture périodique : la page la plus récente s'ajoute au fil déjà affiché
 * (messages précédents compris). Si elle n'a aucun message en commun avec lui
 * — longue absence —, on repart d'elle plutôt que de laisser un trou.
 */
function withLatestPage(
  shown: MessagingThread | undefined,
  page: MessagingThread,
): MessagingThread {
  if (!shown || !page.hasMore) return page;
  const known = new Set(shown.items.map((message) => message.id));
  if (!page.items.some((message) => known.has(message.id))) return page;
  return {
    ...page,
    items: mergeMessages(shown.items, page.items),
    hasMore: shown.hasMore,
  };
}

function withOlderPage(
  shown: MessagingThread,
  older: MessagingThread,
): MessagingThread {
  return {
    ...shown,
    items: mergeMessages(older.items, shown.items),
    hasMore: older.hasMore,
  };
}

function capitalize(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** « Aujourd'hui », « Hier », sinon le jour en toutes lettres. */
function dayLabel(iso: string): string {
  const day = dayjs(iso);
  if (isToday(iso)) return 'Aujourd’hui';
  if (day.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Hier';
  return capitalize(
    day.isSame(dayjs(), 'year') ? fmtDayShort(iso) : fmtDayLong(iso),
  );
}

/** Heure pour aujourd'hui, date sinon. */
function shortDate(iso: string): string {
  if (isToday(iso)) return fmtTime(iso);
  return dayjs(iso).isSame(dayjs(), 'year')
    ? dayjs(iso).format('D MMM')
    : fmtDate(iso);
}

function threadSubtitle(
  kind: MessagingConversationKind,
  members: MessagingMember[],
): string {
  if (kind === 'OFFICE') return 'Le secrétariat de votre auto-école';
  if (kind === 'DIRECT') return 'Moniteur de votre auto-école';
  return members.length > 0
    ? `Groupe · ${members.map((member) => member.name).join(', ')}`
    : 'Groupe';
}

function KindIcon({ kind }: { kind: MessagingConversationKind }) {
  const Icon = kind === 'OFFICE' ? Building2 : kind === 'GROUP' ? Users : User;
  return (
    <span className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
      <Icon size={16} aria-hidden="true" />
    </span>
  );
}

function UnreadPill({ count }: { count: number }) {
  return (
    <span className="relative min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">
      <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
      <span className="sr-only">{` ${count} non lu${count > 1 ? 's' : ''}`}</span>
    </span>
  );
}

function Loading() {
  return (
    <div className="flex-1 flex items-center justify-center p-6 text-sm text-slate-500">
      <Loader2 className="animate-spin mr-2" size={16} aria-hidden="true" />
      Chargement…
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
      <p role="alert" className="text-sm text-red-600">
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
      >
        Réessayer
      </button>
    </div>
  );
}

/** Une relecture a échoué alors que des données sont déjà affichées. */
function RefreshNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex-shrink-0"
    >
      <span>Actualisation impossible pour le moment.</span>
      <button type="button" onClick={onRetry} className="font-semibold underline">
        Réessayer
      </button>
    </div>
  );
}

/** Choix du destinataire : le secrétariat ou un moniteur de l'école, jamais un groupe. */
function NewMessageDialog({
  onClose,
  onOpened,
}: {
  onClose: () => void;
  onOpened: (
    id: string,
    title: string,
    kind: MessagingConversationKind,
  ) => void;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const contacts = useQuery({
    queryKey: ['candidate', 'messaging', 'contacts'],
    queryFn: fetchMessagingContacts,
  });
  const open = useMutation({
    mutationFn: (target: { instructorId: string | null; title: string }) =>
      target.instructorId
        ? openDirectConversation(target.instructorId)
        : openOfficeConversation(),
    onSuccess: ({ id }, target) =>
      onOpened(id, target.title, target.instructorId ? 'DIRECT' : 'OFFICE'),
  });

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    // Le focus reste dans la fenêtre tant qu'elle est ouverte.
    const buttons = panelRef.current?.querySelectorAll<HTMLButtonElement>(
      'button:not([disabled])',
    );
    if (!buttons || buttons.length === 0) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    const current = document.activeElement;
    if (e.shiftKey && (current === first || current === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const instructors = contacts.data?.instructors ?? [];
  const office = !!contacts.data?.office;
  const optionClass =
    'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-slate-50 disabled:opacity-60 transition-colors';

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 shadow-2xl outline-none"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            Nouveau message
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          À qui souhaitez-vous écrire ?
        </p>

        {contacts.isPending ? (
          <Loading />
        ) : contacts.isError ? (
          <ErrorState
            message={serverMessage(
              contacts.error,
              'Impossible de charger vos contacts.',
            )}
            onRetry={() => void contacts.refetch()}
          />
        ) : !office && instructors.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            Aucun contact disponible pour le moment.
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto -mx-3">
            {office && (
              <button
                type="button"
                disabled={open.isPending}
                onClick={() =>
                  open.mutate({ instructorId: null, title: 'Secrétariat' })
                }
                className={optionClass}
              >
                <KindIcon kind="OFFICE" />
                <span className="text-sm font-medium text-slate-900">
                  Secrétariat
                </span>
              </button>
            )}
            {instructors.length > 0 && (
              <>
                <h3 className="px-3 pt-3 pb-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Moniteurs
                </h3>
                <ul>
                  {instructors.map((instructor) => (
                    <li key={instructor.id}>
                      <button
                        type="button"
                        disabled={open.isPending}
                        onClick={() =>
                          open.mutate({
                            instructorId: instructor.id,
                            title: instructor.name,
                          })
                        }
                        className={optionClass}
                      >
                        <KindIcon kind="DIRECT" />
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {instructor.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {open.isError && (
          <p role="alert" className="text-sm text-red-600 mt-3">
            {serverMessage(open.error, 'Impossible d’ouvrir cet échange.')}
          </p>
        )}
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  showAuthor,
  staffTag,
}: {
  message: MessagingMessage;
  /** Faux pour une suite de messages d'un même auteur : le nom n'est lu que par les lecteurs d'écran. */
  showAuthor: boolean;
  staffTag: boolean;
}) {
  return (
    <div className={`flex ${message.mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`relative flex flex-col max-w-[85%] sm:max-w-[70%] ${
          message.mine ? 'items-end' : 'items-start'
        }`}
      >
        {message.mine ? (
          <span className="sr-only">Vous</span>
        ) : (
          <span
            className={
              showAuthor
                ? 'text-xs font-semibold text-slate-600 mb-1 px-1'
                : 'sr-only'
            }
          >
            {message.author}
            {staffTag && (
              <span className="font-normal text-slate-400"> · Auto-école</span>
            )}
          </span>
        )}
        <div
          className={`rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
            message.mine
              ? 'bg-indigo-600 text-white rounded-br-sm'
              : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
          }`}
        >
          {message.body}
        </div>
        <time
          dateTime={message.createdAt}
          className="text-[11px] text-slate-400 mt-1 px-1"
        >
          {fmtTime(message.createdAt)}
        </time>
      </div>
    </div>
  );
}

function ThreadPanel({
  conversationId,
  fallback,
  focusComposer,
  onBack,
}: {
  conversationId: string;
  /** Titre et nature connus avant le chargement du fil. */
  fallback: { title: string; kind: MessagingConversationKind };
  /** Vrai après « Nouveau message » : l'élève vient pour écrire. */
  focusComposer: boolean;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const key = threadKey(conversationId);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  /** Distance au bas du fil à conserver après l'ajout de messages précédents. */
  const anchor = useRef<number | null>(null);
  const stickToBottom = useRef(true);
  /** Dernière séquence déjà signalée comme lue. */
  const marked = useRef(0);
  /** Envoi en cours : le même texte retenté garde le même identifiant. */
  const attempt = useRef<{ requestId: string; body: string } | null>(null);
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);

  const thread = useQuery({
    queryKey: key,
    queryFn: async () => {
      const page = await fetchConversationMessages(conversationId);
      return withLatestPage(
        queryClient.getQueryData<MessagingThread>(key),
        page,
      );
    },
    retry: (failures, error) => !isGone(error) && failures < 1,
    refetchInterval: (query) =>
      isGone(query.state.error) ? false : MESSAGING_REFRESH_MS,
    // Au retour sur l'application, sans attendre la prochaine relecture.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const data = thread.data;
  const items = data?.items ?? [];
  const firstSequence = items.length > 0 ? items[0].sequence : 0;
  const lastSequence = items.length > 0 ? items[items.length - 1].sequence : 0;
  const kind = data?.conversation.kind ?? fallback.kind;
  const title = data?.conversation.title ?? fallback.title;
  // Conversation retirée ou accès fermé : on n'en montre plus rien.
  const failed = thread.isError && (!data || isGone(thread.error));
  const canWrite = !failed && data?.conversation.canWrite !== false;

  const older = useMutation({
    mutationFn: (before: number) =>
      fetchConversationMessages(conversationId, before),
    onSuccess: (page) => {
      const log = logRef.current;
      if (log) anchor.current = log.scrollHeight - log.scrollTop;
      queryClient.setQueryData<MessagingThread>(
        key,
        (shown) => shown && withOlderPage(shown, page),
      );
    },
  });

  const send = useMutation({
    mutationFn: (payload: { requestId: string; body: string }) =>
      sendConversationMessage(conversationId, payload),
    onSuccess: (sent, payload) => {
      attempt.current = null;
      // Le serveur tient pour lu tout ce qui précède le message envoyé.
      marked.current = Math.max(marked.current, sent.sequence);
      stickToBottom.current = true;
      setDraft('');
      // Affichage immédiat, remplacé par la version du serveur à la relecture.
      queryClient.setQueryData<MessagingThread>(
        key,
        (shown) =>
          shown && {
            ...shown,
            items: mergeMessages(shown.items, [
              {
                id: sent.id,
                sequence: sent.sequence,
                mine: true,
                authorKind: 'student',
                author: '',
                body: payload.body,
                createdAt: new Date().toISOString(),
              },
            ]),
          },
      );
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      inputRef.current?.focus();
    },
    onError: (error) =>
      setSendError(
        serverMessage(error, 'Votre message n’a pas pu être envoyé.'),
      ),
  });

  const submit = () => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    if (attempt.current?.body !== body)
      attempt.current = { requestId: newRequestId(), body };
    setSendError(null);
    send.mutate(attempt.current);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  };

  // À l'ouverture : la saisie après « Nouveau message » ; sur téléphone, où la
  // liste disparaît, le titre du fil.
  useEffect(() => {
    if (focusComposer) inputRef.current?.focus();
    else if (backRef.current?.offsetParent) headingRef.current?.focus();
  }, [focusComposer]);

  // Le fil reste calé en bas, sauf si l'élève est remonté lire ; les messages
  // précédents s'ajoutent sans déplacer ce qu'il lit.
  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log) return;
    if (anchor.current !== null) {
      log.scrollTop = log.scrollHeight - anchor.current;
      anchor.current = null;
    } else if (stickToBottom.current) {
      log.scrollTop = log.scrollHeight;
    }
  }, [firstSequence, lastSequence]);

  // La zone de saisie grandit avec le texte, jusqu'à quelques lignes.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const borders = input.offsetHeight - input.clientHeight;
    input.style.height = `${Math.min(input.scrollHeight + borders, 136)}px`;
  }, [draft]);

  // Fil ouvert : lu jusqu'au dernier message affiché.
  useEffect(() => {
    if (lastSequence <= marked.current) return;
    const previous = marked.current;
    marked.current = lastSequence;
    markConversationRead(conversationId, lastSequence)
      .then(() => {
        queryClient.setQueryData<MessagingConversation[]>(
          CONVERSATIONS_KEY,
          (list) =>
            list?.map((c) => (c.id === conversationId ? { ...c, unread: 0 } : c)),
        );
        void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      })
      .catch(() => {
        // Sans gravité : nouvel essai au prochain message reçu.
        if (marked.current === lastSequence) marked.current = previous;
      });
  }, [conversationId, lastSequence, queryClient]);

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 flex-shrink-0">
        <button
          ref={backRef}
          type="button"
          onClick={onBack}
          aria-label="Retour aux conversations"
          className="lg:hidden p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <KindIcon kind={kind} />
        <div className="min-w-0">
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="text-base font-semibold text-slate-900 truncate outline-none"
          >
            {title}
          </h2>
          <p
            className="text-xs text-slate-500 truncate"
            title={kind === 'GROUP' ? threadSubtitle(kind, data?.members ?? []) : undefined}
          >
            {threadSubtitle(kind, data?.members ?? [])}
          </p>
        </div>
      </div>

      {thread.isError && !failed && (
        <RefreshNotice onRetry={() => void thread.refetch()} />
      )}

      {failed ? (
        <ErrorState
          message={serverMessage(
            thread.error,
            'Impossible de charger cette conversation.',
          )}
          onRetry={() => void thread.refetch()}
        />
      ) : !data ? (
        <Loading />
      ) : (
        <div
          ref={logRef}
          role="log"
          aria-label="Fil de discussion"
          tabIndex={0}
          onScroll={(e) => {
            const log = e.currentTarget;
            stickToBottom.current =
              log.scrollHeight - log.scrollTop - log.clientHeight < 80;
          }}
          className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/60"
        >
          {data.hasMore && (
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                disabled={older.isPending}
                onClick={() => older.mutate(firstSequence)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors"
              >
                {older.isPending && (
                  <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                )}
                Messages précédents
              </button>
              {older.isError && (
                <p role="alert" className="text-xs text-red-600">
                  {serverMessage(
                    older.error,
                    'Les messages précédents n’ont pas pu être chargés.',
                  )}
                </p>
              )}
            </div>
          )}
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-8">
              <p className="text-sm font-medium text-slate-700">
                Aucun message pour le moment.
              </p>
              {canWrite && (
                <p className="text-sm text-slate-500">
                  Écrivez le premier message ci-dessous.
                </p>
              )}
            </div>
          ) : (
            items.map((message, index) => {
              const previous = index > 0 ? items[index - 1] : null;
              const newDay =
                !previous || !isSameDay(previous.createdAt, message.createdAt);
              const showAuthor =
                newDay ||
                previous.mine ||
                previous.author !== message.author ||
                previous.authorKind !== message.authorKind;
              return (
                <div key={message.id} className="space-y-3">
                  {newDay && (
                    <p className="text-center text-[11px] font-medium text-slate-500">
                      {dayLabel(message.createdAt)}
                    </p>
                  )}
                  <MessageBubble
                    message={message}
                    showAuthor={showAuthor}
                    staffTag={kind === 'GROUP' && message.authorKind === 'staff'}
                  />
                </div>
              );
            })
          )}
        </div>
      )}

      {!failed && !canWrite && (
        <p className="px-4 py-3 border-t border-slate-200 text-sm text-slate-500 text-center flex-shrink-0">
          Vous ne pouvez pas écrire dans cette conversation.
        </p>
      )}
      {canWrite && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="border-t border-slate-200 p-3 flex-shrink-0"
        >
          {sendError && (
            <div
              role="alert"
              className="mb-2 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              <span>{sendError}</span>
              <button
                type="button"
                onClick={submit}
                className="font-semibold underline flex-shrink-0"
              >
                Réessayer
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              aria-label="Votre message"
              placeholder="Écrivez votre message…"
              rows={1}
              maxLength={MAX_BODY_LENGTH}
              value={draft}
              readOnly={send.isPending}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              className="flex-1 min-w-0 resize-none px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
            <button
              type="submit"
              aria-label="Envoyer le message"
              disabled={!draft.trim() || send.isPending}
              className="p-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl shadow-sm transition-colors flex-shrink-0"
            >
              {send.isPending ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <Send size={16} aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="mt-1.5 flex justify-between gap-3 text-[11px] text-slate-400">
            <span className="hidden sm:inline">
              Entrée pour envoyer, Maj + Entrée pour aller à la ligne.
            </span>
            {draft.length >= MAX_BODY_LENGTH - 200 && (
              <span className="ml-auto">
                {draft.length} / {MAX_BODY_LENGTH}
              </span>
            )}
          </div>
        </form>
      )}
    </>
  );
}

interface Selection {
  id: string;
  title: string;
  kind: MessagingConversationKind;
  /** Ouverte depuis « Nouveau message ». */
  compose: boolean;
}

export default function MessagesView() {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [choosing, setChoosing] = useState(false);
  const newButtonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  /** Conversation à retrouver dans la liste au retour du fil (téléphone). */
  const returnTo = useRef<string | null>(null);

  const conversationsQuery = useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: fetchConversations,
    refetchInterval: MESSAGING_REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const conversations = conversationsQuery.data ?? [];

  // La carte descend jusqu'au bas de l'écran, moins la bande où flotte le
  // bouton de l'assistant. Sa hauteur est mesurée : bandeaux et onglets
  // au-dessus d'elle varient d'un élève et d'un écran à l'autre.
  const threadOpen = selection !== null;
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const card = cardRef.current;
      if (!card) return;
      const top = card.getBoundingClientRect().top + window.scrollY;
      setCardHeight(Math.round(window.innerHeight - top - BOTTOM_RESERVE));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [threadOpen]);

  useEffect(() => {
    if (selection || !returnTo.current) return;
    const id = returnTo.current;
    returnTo.current = null;
    Array.from(listRef.current?.querySelectorAll('button') ?? [])
      .find((button) => button.dataset.conversation === id)
      ?.focus();
  }, [selection]);

  return (
    <div className="flex flex-col gap-6 pb-20">
      <div
        className={`flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${
          selection ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <div>
          <h1 className="text-slate-900">Messages</h1>
          <p className="text-slate-600">
            Écrivez au secrétariat ou aux moniteurs de votre auto-école.
          </p>
        </div>
        <button
          ref={newButtonRef}
          type="button"
          onClick={() => setChoosing(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors flex-shrink-0"
        >
          <SquarePen size={16} aria-hidden="true" />
          Nouveau message
        </button>
      </div>

      {/* Téléphone : la liste, à sa hauteur naturelle, puis le fil.
          Grand écran : les deux côte à côte. */}
      <div
        ref={cardRef}
        style={
          cardHeight === null
            ? undefined
            : ({ '--card-height': `${cardHeight}px` } as CSSProperties)
        }
        className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden lg:grid lg:grid-cols-[20rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:h-[var(--card-height)] lg:min-h-[20rem] ${
          selection ? 'h-[var(--card-height)] min-h-[20rem]' : ''
        }`}
      >
        <section
          aria-label="Conversations"
          className={`flex-col min-h-0 lg:border-r lg:border-slate-200 ${
            selection ? 'hidden lg:flex' : 'flex'
          }`}
        >
          {conversationsQuery.isError && conversationsQuery.data && (
            <RefreshNotice onRetry={() => void conversationsQuery.refetch()} />
          )}
          {conversationsQuery.isPending ? (
            <Loading />
          ) : conversationsQuery.isError && !conversationsQuery.data ? (
            <ErrorState
              message={serverMessage(
                conversationsQuery.error,
                'Impossible de charger vos conversations.',
              )}
              onRetry={() => void conversationsQuery.refetch()}
            />
          ) : conversations.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-1 px-6 py-10 text-center">
              <MessageCircle
                size={28}
                className="text-slate-300 mb-2"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-slate-700">
                Aucune conversation pour le moment.
              </p>
              <p className="text-sm text-slate-500">
                Utilisez « Nouveau message » pour écrire au secrétariat ou à un
                moniteur.
              </p>
            </div>
          ) : (
            <ul
              ref={listRef}
              className="flex-1 min-h-0 lg:overflow-y-auto divide-y divide-slate-100"
            >
              {conversations.map((c) => {
                const active = selection?.id === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      data-conversation={c.id}
                      aria-current={active ? 'true' : undefined}
                      onClick={() =>
                        setSelection({
                          id: c.id,
                          title: c.title,
                          kind: c.kind,
                          compose: false,
                        })
                      }
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        active ? 'bg-indigo-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <KindIcon kind={c.kind} />
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-2">
                          <span
                            className={`truncate text-sm ${
                              c.unread > 0
                                ? 'font-semibold text-slate-900'
                                : 'font-medium text-slate-800'
                            }`}
                          >
                            {c.title}
                          </span>
                          {c.kind === 'GROUP' && (
                            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-semibold text-slate-600 uppercase tracking-wide flex-shrink-0">
                              Groupe
                            </span>
                          )}
                          {c.last && (
                            <time
                              dateTime={c.last.at}
                              className="ml-auto text-[11px] text-slate-400 flex-shrink-0"
                            >
                              {shortDate(c.last.at)}
                            </time>
                          )}
                        </span>
                        <span className="flex items-center gap-2 mt-0.5">
                          <span
                            className={`flex-1 truncate text-xs ${
                              c.unread > 0 ? 'text-slate-700' : 'text-slate-500'
                            }`}
                          >
                            {c.last ? c.last.body : 'Aucun message pour le moment'}
                          </span>
                          {c.unread > 0 && <UnreadPill count={c.unread} />}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section
          aria-label={selection ? `Conversation : ${selection.title}` : 'Conversation'}
          className={`flex-col min-h-0 h-full ${
            selection ? 'flex' : 'hidden lg:flex'
          }`}
        >
          {selection ? (
            <ThreadPanel
              key={selection.id}
              conversationId={selection.id}
              fallback={{ title: selection.title, kind: selection.kind }}
              focusComposer={selection.compose}
              onBack={() => {
                returnTo.current = selection.id;
                setSelection(null);
              }}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 p-6 text-center">
              <MessageCircle
                size={32}
                className="text-slate-300"
                aria-hidden="true"
              />
              <p className="text-sm text-slate-500">
                {conversations.length > 0
                  ? 'Choisissez une conversation pour lire vos messages.'
                  : 'Vos échanges avec votre auto-école s’afficheront ici.'}
              </p>
            </div>
          )}
        </section>
      </div>

      {choosing && (
        <NewMessageDialog
          onClose={() => {
            setChoosing(false);
            newButtonRef.current?.focus();
          }}
          onOpened={(id, title, kind) => {
            setChoosing(false);
            setSelection({ id, title, kind, compose: true });
            void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
          }}
        />
      )}
    </div>
  );
}
